import { forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Heading4 } from '@/components/ui/typography'
import { prefixGroupFilterValues } from '@/routes/Records/IB/Rebates/helpers/prefixGroupFilterValues'
import { CompositeFilterDescriptor } from '@progress/kendo-data-query'
import { FieldSettings, Filter, FilterChangeEvent } from '@progress/kendo-react-data-tools'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridPageChangeEvent,
} from '@progress/kendo-react-grid'
import { NameDto } from 'api/Common/Types'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { Pagination } from 'api/Pagination/Types/Pagination'
import { IntroducingBrokerDetailsDto } from 'api/Trading-Accounts/Types/IntroducingBrokerDetailsDto'
import {
  CreateRebateGroup,
  IntroducingBrokerRebateGroup,
} from 'api/Trading-Accounts/Types/IntroducingBrokerRebates'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { FilterCell } from 'components/Filter/FilterCell'
import {
  FormBaseButtonContainer,
  FormBaseColumn,
  FormBaseFieldLabel,
  FormBaseRow,
  FormBaseSectionBlock,
} from 'components/Form/Form.styles'
import { Loading } from 'components/Loading'
import MoreButton from 'components/MoreButton/MoreButton'
import { useSingleGridState } from 'features/DataGrid/hooks/useGridState'
import { Search } from 'lucide-react'
import { pageableSettings } from 'store/grid/grid.slice'
import { useApiClient } from 'utils/api/ApiClient'
import { PartnershipsApiClient } from 'utils/api/partnershipsApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { AuthSessionContext } from 'utils/AuthContext'
import { hasPermission } from 'utils/permissionsValidator'
import { buildQuery } from 'utils/QueryHelpers'

import { CreateRebateGroupModal } from './modals/CreateRebateGroupModal'

interface Props {
  data: IntroducingBrokerDetailsDto
  isEdit: boolean
  refreshRebates?: () => void
  setGroups: (groups: NameDto<string>[]) => void
  groupFilter?: CompositeFilterDescriptor
  onGroupFilterChange?: (event: FilterChangeEvent) => void
  groupFilterFields?: FieldSettings[]
}

export const IBRebatesGroupSettings = forwardRef((props: Props, ref) => {
  const {
    data,
    isEdit,
    setGroups,
    refreshRebates,
    groupFilter,
    onGroupFilterChange,
    groupFilterFields,
  } = props
  type ModalOption = 'create-group' | 'delete-group' | 'edit-group' | ''
  const [visibleModal, setVisibleModal] = useState<ModalOption>()
  const { gridState, gridActions } = useSingleGridState()
  const partnershipsApiClient = useApiClient(PartnershipsApiClient)
  const [auth] = useContext(AuthSessionContext)
  const [editGroupModal, setEditGroupModal] = useState<{
    visible: boolean
    data: IntroducingBrokerRebateGroup
    id: string
  }>()
  const [dataSelected, setDataSelected] = useState<IntroducingBrokerRebateGroup[]>()

  const groupFilterRef = useRef<CompositeFilterDescriptor | undefined>(undefined)
  useEffect(() => {
    groupFilterRef.current = props.groupFilter
  }, [props.groupFilter])

  const permissions = auth?.permissions || []

  const { callback: rebateGroupsCallback, forceRefresh } = useCallbackWithForceRefresh(async () => {
    const currentFilter = groupFilterRef.current
    const prefixedFilter = currentFilter ? prefixGroupFilterValues(currentFilter, data.id) : null
    const searchQuery = prefixedFilter ? (buildQuery(prefixedFilter) ?? null) : null

    const response = await partnershipsApiClient.getIntroducingBrokerRebateGroups(data.id, {
      pageIndex: gridState.pageIndex,
      pageSize: gridState.pageSize,
      search: searchQuery,
    })
    setGroups(response.items?.map((item) => ({ id: item.id, name: item.name })))
    return response
  }, [data.id, gridState])

  const { data: rebateGroups, isLoading } = useFetchOne(rebateGroupsCallback)

  useImperativeHandle(ref, () => ({
    refreshGroups() {
      forceRefresh()
    },
    resetSelection() {
      setDataSelected([])
    },
  }))

  const handleFilterFetch = () => {
    forceRefresh()
  }

  const handleGridPageChange = async (event: GridPageChangeEvent) => {
    const { skip, take } = event.page
    const pagination = new Pagination({
      skip,
      recordsPerPage: take,
    })

    const pageIndex = !event.nativeEvent ? 1 : pagination.getPageIndex()
    const pageSize = !event.nativeEvent ? take : pagination.getRecordsPerPage()
    gridActions.setGridFilters({ take, skip, pageIndex, pageSize })
  }

  const createRebateGroup = async (rebateData: CreateRebateGroup) => {
    setVisibleModal(undefined)
    await partnershipsApiClient.createRebateGroup(data.id, rebateData)
    gridActions.resetGridFilters()
  }

  const deleteRebateGroup = async () => {
    setVisibleModal(undefined)
    if (dataSelected?.length) {
      await partnershipsApiClient.deleteRebateGroups(
        data.id,
        dataSelected.map((item) => item.id)
      )
      gridActions.resetGridFilters()
      refreshRebates?.()
      setDataSelected([])
    }
  }

  const editRebateGroup = async (rebateData: CreateRebateGroup, id: string) => {
    await partnershipsApiClient.updateRebateGroup(data.id, id, rebateData)
    gridActions.resetGridFilters()
    refreshRebates?.()
    setEditGroupModal(undefined)
  }

  return (
    <FormBaseColumn>
      {groupFilter && onGroupFilterChange && groupFilterFields && (
        <div style={{ marginBottom: 16 }}>
          <Heading4 style={{ marginBottom: 8 }}>Group Settings Filters</Heading4>
          <Filter
            style={{ zIndex: 0 }}
            value={groupFilter}
            onChange={onGroupFilterChange}
            fields={groupFilterFields}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
            <Button
              type='button'
              onClick={handleFilterFetch}
              data-test='group-filter-search-button'
            >
              <Search />
              Filter Groups
            </Button>
          </div>
        </div>
      )}
      <ConfirmationModal
        contentText={`Are you sure you want to delete the group(s) ${dataSelected?.map((item) => item.name).join(', ')}?`}
        yesButtonText='Yes'
        cancelButtonText='No'
        onYes={deleteRebateGroup}
        onCancel={() => setVisibleModal(undefined)}
        visible={visibleModal === 'delete-group'}
      />
      {visibleModal === 'create-group' && (
        <CreateRebateGroupModal
          isOpen
          onClose={() => setVisibleModal(undefined)}
          onSubmit={createRebateGroup}
        />
      )}
      {editGroupModal?.visible && (
        <CreateRebateGroupModal
          isOpen
          onClose={() => setEditGroupModal(undefined)}
          onUpdate={editRebateGroup}
          isEdit
          data={editGroupModal.data}
          id={editGroupModal.id}
        />
      )}
      <FormBaseRow>
        <FormBaseSectionBlock>
          <FormBaseFieldLabel>Group Settings</FormBaseFieldLabel>
        </FormBaseSectionBlock>
        <FormBaseButtonContainer>
          <Button
            type='button'
            style={{ marginRight: 'auto' }}
            onClick={() => setVisibleModal('create-group')}
            disabled={
              !hasPermission(permissions, PermissionsEnum.CanEditIntroducingBrokerRebate) || !isEdit
            }
            data-test='create-rebate-group-button'
          >
            Create
          </Button>
          <Button
            type='button'
            style={{ marginRight: 'auto' }}
            onClick={() => setVisibleModal('delete-group')}
            disabled={
              !hasPermission(permissions, PermissionsEnum.CanEditIntroducingBrokerRebate) ||
              !isEdit ||
              !dataSelected?.length
            }
            data-test='delete-rebate-group-button'
          >
            Delete
          </Button>
        </FormBaseButtonContainer>
      </FormBaseRow>

      <Loading isLoading={isLoading}>
        <Grid
          {...gridState}
          data={rebateGroups?.items}
          pageable={pageableSettings}
          onPageChange={handleGridPageChange}
          selectedField='selected'
          onSelectionChange={() => {}}
          sort={gridState.sort}
          total={rebateGroups?.totalResults}
          take={gridState.take}
          skip={gridState.skip}
        >
          <Column
            field='selected'
            width='45px'
            cell={({ dataItem }) => (
              <td>
                <input
                  disabled={!isEdit}
                  type='checkbox'
                  checked={isEdit && dataSelected?.some((item) => item.id === dataItem.id)}
                  onChange={(event) =>
                    event.target.checked
                      ? setDataSelected([...(dataSelected || []), dataItem])
                      : setDataSelected(dataSelected?.filter((item) => item.id !== dataItem.id))
                  }
                />
              </td>
            )}
            headerCell={() => <th></th>}
          />
          <Column className='table-columns__content' field='name' title='Group' />
          <Column
            className='table-columns__content'
            field='rebateApprovalType.name'
            title='Rebate type'
          />
          <Column
            className='table-columns__content'
            field='rebatePeriod.name'
            title='Rebate period'
          />
          <Column
            className='table-columns__content'
            field='rebateAmountInPercentage'
            title='Rebate amount, %'
          />
          <Column
            className='table-columns__content'
            field='tradingAccountsCount'
            title='TAs count'
          />
          <Column
            className='table-columns__content'
            field='isDefault'
            title='Default'
            cell={({ dataItem }) => (
              <td className='table-columns__date'>
                <input type='checkbox' checked={dataItem.isDefault} readOnly />
              </td>
            )}
          />

          {hasPermission(permissions, PermissionsEnum.CanEditIntroducingBrokerRebate) && (
            <Column
              width={50}
              title=''
              filterCell={FilterCell}
              cell={(props: GridCellProps) => (
                <td>
                  <MoreButton
                    disabled={!isEdit}
                    items={[
                      {
                        name: 'Edit',
                        'data-test': 'edit',

                        onClick: () =>
                          setEditGroupModal({
                            visible: true,
                            data: props.dataItem,
                            id: props.dataItem.id,
                          }),
                      },
                    ]}
                    data-test='more-button'
                  />
                </td>
              )}
            />
          )}
        </Grid>
      </Loading>
    </FormBaseColumn>
  )
})
